import React, { useMemo } from "react";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import AcxButton from "components/UI/AcxButton/AcxButton";
import { Link } from "react-router-dom";

export type BreadcrumbProps = {
    children: React.ReactNode;
    href?: string;
    isActive: boolean;
    onClick?: () => void;
};

const fontStyle = {
    fontSize: 14,
    fontWeight: 600,
};

const Breadcrumb: React.FC<BreadcrumbProps> = ({
    children,
    isActive,
    href = "",
    onClick,
}) => {
    if (isActive) {
        return (
            <Typography
                sx={{ color: theme.palette.blackFont.main, ...fontStyle }}
            >
                {children}
            </Typography>
        );
    }

    if (onClick) {
        return (
            <AcxButton
                disableRipple
                sx={{
                    background: "transparent",
                    color: theme.palette.gray[400],
                    padding: 0,
                    height: "inherit",
                    minWidth: "unset",
                    ":hover": {
                        background: "transparent",
                    },
                    ...fontStyle,
                }}
                onClick={onClick}
            >
                {children}
            </AcxButton>
        );
    }

    return (
        <Typography
            sx={{
                a: {
                    color: theme.palette.gray[400],
                    textDecoration: "none",
                    ...fontStyle,
                },
            }}
        >
            <Link className="pendo-ignore" to={href}>{children}</Link>
        </Typography>
    );
};

export type BreadcrumbType = {
    label: string;
    link?: string;
    onClick?: () => void;
};

export type BreadcrumbsProps = {
    additionalBreadcrumbs?: BreadcrumbType[];
    pageTitle?: string;
    /** Middle section crumb label. Defaults to "Organization Setup". */
    sectionLabel?: string;
};

const OrganizationBreadcrumbs: React.FC<BreadcrumbsProps> = ({
    additionalBreadcrumbs,
    pageTitle,
    sectionLabel = "Organization Setup",
}) => {
    const authStore = useStore(AuthStore);

    const breadcrumbs = useMemo(() => {
        const baseLink = `/admin/organizations/${authStore.activeOrgId || ""}`;
        const baseBreadcrumbs: BreadcrumbType[] = [
            { label: "Admin", link: baseLink },
            { label: sectionLabel, link: baseLink },
        ];

        if (pageTitle) {
            baseBreadcrumbs.push({ label: pageTitle });
        }

        if (additionalBreadcrumbs) {
            baseBreadcrumbs.push(...additionalBreadcrumbs);
        }

        return baseBreadcrumbs;
    }, [authStore.activeOrgId, pageTitle, additionalBreadcrumbs, sectionLabel]);

    return (
        <Breadcrumbs
            separator={
                <NavigateNextIcon
                    htmlColor={theme.palette.gray[400]}
                    fontSize="small"
                />
            }
        >
            {breadcrumbs.map((breadcrumb, index) => (
                <Breadcrumb
                    key={index}
                    isActive={index === breadcrumbs.length - 1}
                    href={breadcrumb.link}
                    onClick={breadcrumb.onClick}
                >
                    {breadcrumb.label}
                </Breadcrumb>
            ))}
        </Breadcrumbs>
    );
};

export default OrganizationBreadcrumbs;
